import { Modal } from '@simplywallst/ui-core';

import { Heading, Button } from '@simplywallst/ui-core/next';
import CloseIconSVG from '@simplywallst/ui-core/icons/CrossSolid';
import { useHasPortfolios } from '../../hooks/useHasPortfolios';
import { NewLinkedPortfolio } from '@/features/portfolio/pages/NewPortfolio/components/NewLinkedPortfolio';
import { DeprecatedManualPortfolio } from '@/features/portfolio/pages/NewPortfolio/components/NewManualPortfolio/DeprecatedManualPortfolio';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useState } from 'react';
import { useTracking } from 'react-tracking';
import { DemoPortfolioLink } from '../DemoPortfolioLink';

type Props = {
  completeOnboarding: () => void;
};

export const OnboardingModal = ({ completeOnboarding }: Props) => {
  const { hasPortfolios } = useHasPortfolios();
  const { data: userAccess } = useUserAccess();
  const [isOpen, setIsOpen] = useState(true);
  const { Track } = useTracking({
    type: 'track',
    subject: 'portfolioOnboardingModal',
  });

  const handleClose = () => {
    completeOnboarding();
    setIsOpen(false);
  };

  if (hasPortfolios || !isOpen) return;

  return (
    <Track>
      <Modal fullscreen isNaked data-cy-id="onboarding-modal-wrapper">
        <div className="fixed inset-0 bg-surface-1 lg:hidden" />
        <div className="relative grid w-full max-w-[830px] self-center">
          <div className="absolute inset-0 hidden rounded-lg bg-surface-1 lg:block" />
          <div className="relative overflow-y-auto overflow-x-hidden">
            <section className="w-full py-x2 text-solid @container/newPortfolio sm:p-x3">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-x0_5 focus-visible:ring"
                >
                  <CloseIconSVG className="fill-solid" />
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-4 sm:px-0 md:pb-6">
                <div className="grid gap-x1">
                  <Heading size="xLarge">
                    Create your first portfolio in minutes
                  </Heading>
                  {['free', null].includes(userAccess.planType) && (
                    <div className="max-w-[55ch] text-xs text-softer">
                      Free forever for up to 10 holdings.
                    </div>
                  )}
                </div>
                <div className="flex">
                  <DemoPortfolioLink setIsOpen={setIsOpen} />
                </div>
              </div>
              <div className="grid gap-x2 @5xl/newPortfolio:grid-cols-[61.8%_auto] sm:gap-x3">
                <section className="flex flex-col gap-x2 bg-surface-2 py-x3 [--draggable-bg:--s-color-surface-2] sm:gap-x3 sm:rounded-lg">
                  <NewLinkedPortfolio />
                </section>
                <section className="overflow-hidden bg-surface-2 p-x2 sm:rounded-lg sm:p-x3">
                  <DeprecatedManualPortfolio />
                </section>
              </div>
              <div className="mt-x2 grid justify-end">
                <Button onClick={handleClose} variant="secondary" size="medium">
                  Skip, I’ll do this later
                </Button>
              </div>
            </section>
          </div>
        </div>
      </Modal>
    </Track>
  );
};
